import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import { FlexBox, responsive, Typography, useScreenClass } from '@vp/swan';
import { DateFormatter, DateFormatTypes } from '@vp/om-date-formatter';
import type { ExpectedDeliveryMessageProps } from '../../types/ExpectedDeliveryMessageProps.types';
import { useSiteData } from '../../hooks/useSiteData';

/**
 * Displays expected delivery message and date.
 * Since this is a shared components whose consumers have different styling norms,
 * this component accepts an optional style override object.
 * @param props.expectedDeliveryDate - date string in ISO format
 * @param props.textColorOverride - optional text color override, defaults to 'standard'
 */
export const ExpectedDeliveryMessage = (
  props: ExpectedDeliveryMessageProps
) => {
  const { expectedDeliveryDate, textColorOverride } = props;
  const { locale } = useSiteData();

  const screenSize = useScreenClass();

  const [fulfillmentStatusMessages = {}] = useTranslations([
    'fulfillmentStatusMessages',
  ]);

  if (expectedDeliveryDate.length === 0) {
    return null;
  }

  const flexDirection = ['xs', 'sm', 'md'].includes(screenSize)
    ? 'column'
    : 'row';

  // Add spacing between message and date when displayed in row
  // Cannot use ResponsiveTypography to set this, because responsive depends on screenSize,
  // and this value depends on whether the message and date are being rendered in a row.
  const marginLeft = flexDirection === 'row' ? 2 : 0;

  const ResponsiveFlexBox = responsive(FlexBox);

  return (
    <Typography
      fontSize={'standard'}
      fontWeight={'bold'}
      textColor={textColorOverride || 'standard'}
      data-testid="expected-delivery-message"
    >
      <ResponsiveFlexBox flexDirection={'column'} lg={{ flexDirection: 'row' }}>
        <ReplaceTokens
          tokenizedString={fulfillmentStatusMessages.expectedDeliveryMessage}
          substitutions={{
            date: (
              <Typography ml={marginLeft}>
                <DateFormatter
                  date={expectedDeliveryDate}
                  formatType={DateFormatTypes.SHORT_DATE_W_DOW}
                  locale={locale}
                />
              </Typography>
            ),
          }}
        />
      </ResponsiveFlexBox>
    </Typography>
  );
};
